import { CalendarEvent, EVENT_CATEGORIES } from "../types/event";

/**
 * Excel export of the calendar's current view.
 *
 * "Current view" means what is on screen: the events the calendar fetched for
 * the page being shown, after the left rail's category, title and location
 * filters. Not the whole year, and not the unfiltered fetch — someone who has
 * narrowed the calendar down to Edumate events in the Gym has already said what
 * they want a list of, and an export that ignores that is a different question's
 * answer.
 *
 * exceljs is imported on demand. It is roughly a megabyte and most visits to
 * the calendar never export, so it stays out of the initial bundle — the same
 * reason the attendee roll's export does it.
 *
 * Dates are written as real Excel dates rather than formatted strings, so they
 * sort and filter as dates in the spreadsheet. Stored instants are local-naive
 * school time, which `new Date()` reads as local, so no timezone conversion
 * happens on the way out.
 */

/** Midnight to midnight is how this app stores a date-only event. */
const isAllDay = (start: Date | null, end: Date | null): boolean => {
	const atMidnight = (d: Date | null) =>
		!!d && d.getHours() === 0 && d.getMinutes() === 0;
	return atMidnight(start) && atMidnight(end);
};

const asDate = (value: string | undefined): Date | null => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * A LISS event is still LABELLED `MyPymble` until the Edval sync work relabels
 * it, even though the rail selects it as `liss`. Without this line an exported
 * timetable row reads "mypymble" — a raw id, in an export someone sends on.
 * Delete it when `_categorize_events` starts emitting "LISS".
 */
const LEGACY_LABELS: Record<string, string> = { mypymble: "Timetable" };

/** The label the filter rail shows, not the raw category id. */
const categoryLabel = (id: string): string =>
	EVENT_CATEGORIES.find((c) => c.id === id)?.label ?? LEGACY_LABELS[id] ?? id;

/** `Events 2026-08-01 to 2026-08-31.xlsx`, minus anything illegal in a name. */
const fileName = (from: Date | null, to: Date | null): string => {
	const part = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : "");
	const range = from && to ? ` ${part(from)} to ${part(to)}` : "";
	return `Events${range}.xlsx`.replace(/[/\\?%*:|"<>]/g, "-");
};

export interface ExportRange {
	from: Date | null;
	to: Date | null;
}

/**
 * Build the workbook and hand it to the browser.
 *
 * Returns false when there is nothing to export, so the caller can say so
 * rather than downloading an empty sheet.
 */
export async function exportEventsToExcel(
	events: CalendarEvent[],
	range: ExportRange,
): Promise<boolean> {
	if (!events.length) return false;

	const ExcelJS = await import("exceljs");
	const book = new ExcelJS.Workbook();
	const sheet = book.addWorksheet("Events");

	sheet.columns = [
		{ header: "Title", key: "title", width: 42 },
		{ header: "Starts", key: "start", width: 20 },
		{ header: "Ends", key: "end", width: 20 },
		{ header: "All day", key: "allDay", width: 9 },
		{ header: "Calendar", key: "category", width: 14 },
		{ header: "Location", key: "location", width: 26 },
		{ header: "Address", key: "address", width: 34 },
		{ header: "Released to carers", key: "released", width: 18 },
		{ header: "Released at", key: "releasedAt", width: 20 },
		/* Last, and narrow: nobody reads it, but it is what support asks for
		   when someone reports a problem with a row. */
		{ header: "Event id", key: "id", width: 26 },
	];

	const header = sheet.getRow(1);
	header.font = { bold: true };
	sheet.views = [{ state: "frozen", ySplit: 1 }];

	// Chronological, which is the order the calendar implies but the filtered
	// array does not guarantee.
	const ordered = [...events].sort((a, b) =>
		(a.StartDateTime ?? "").localeCompare(b.StartDateTime ?? ""),
	);

	for (const evt of ordered) {
		const start = asDate(evt.StartDateTime);
		const end = asDate(evt.EndDateTime);
		const allDay = isAllDay(start, end);
		const releasedAt = asDate(evt.ActiveAt ?? undefined);

		sheet.addRow({
			title: evt.Title || evt.title || "",
			start,
			end,
			allDay: allDay ? "Yes" : "No",
			category: categoryLabel(evt.event_type),
			location: evt.Location?.Name ?? "",
			address: evt.Location?.Address ?? "",
			released: evt.ActiveAt ? "Yes" : "No",
			releasedAt,
			id: evt._id ?? "",
		});
	}

	/* A date-only event has no meaningful time, so showing 00:00 against it
	   would be noise dressed up as information. */
	sheet.getColumn("start").numFmt = "dd/mm/yyyy hh:mm";
	sheet.getColumn("end").numFmt = "dd/mm/yyyy hh:mm";
	sheet.getColumn("releasedAt").numFmt = "dd/mm/yyyy hh:mm";
	sheet.eachRow((row, number) => {
		if (number === 1) return;
		if (row.getCell("allDay").value === "Yes") {
			row.getCell("start").numFmt = "dd/mm/yyyy";
			row.getCell("end").numFmt = "dd/mm/yyyy";
		}
	});

	sheet.autoFilter = {
		from: { row: 1, column: 1 },
		to: { row: 1, column: sheet.columns.length },
	};

	const buffer = await book.xlsx.writeBuffer();
	const url = URL.createObjectURL(
		new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		}),
	);
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName(range.from, range.to);
	link.click();
	URL.revokeObjectURL(url);
	return true;
}
